import SEO from "@/components/SEO";
import { Reveal, Eyebrow, PageBand, CTABand, GoldRule } from "@/components/Shared";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FIRST_VISIT_FAQS } from "@/data/content";
import { PAGE_HEROES } from "@/data/photos";

const SECTIONS = [
  {
    title: "Before you arrive",
    body: "Book online or by enquiry and we'll send you a short form to complete beforehand — history, medications, what you're considering. Bring a list of anything you take or use on your skin, and come bare-faced if you can. Free parking is on Marine Parade, directly outside.",
  },
  {
    title: "The consultation",
    body: "Forty-five minutes with a doctor (twenty for a skin assessment), or thirty with our skin & cosmetic nurse. Your history, an examination, an honest assessment of what would help and what wouldn't, and a screening conversation where guidelines require it. You leave with a written plan — options, costs and risks — whether or not you ever act on it.",
  },
  {
    title: "What it costs",
    body: "Physician consultation — any physician, any concern — 45 minutes, $250. Skin assessment with Dr Kiran Shahid — 45 minutes, $250. Hair assessment with Dr Kristy Truong — 45 minutes, $250. Body and wellness assessment with Dr Anthony Chaffee — 45 minutes, $250. Skin consultation with Lizzie Stock — 45 minutes, $100. Every fee is redeemable against treatment within 30 days; a deposit ($100 physician, $50 nurse) is taken at booking and credited to your consultation. Reviews for existing patients are free. Prescription treatments are quoted in writing before anything is booked.",
  },
  {
    title: "If we say no",
    body: "'Not yet' and 'not at all' are normal outcomes of a consultation here. You will not be charged for a treatment we decline to provide, and we'll tell you what would help instead — including when that's nothing, or time.",
  },
  {
    title: "Booking your treatment",
    body: "When you're ready — days or months later, it doesn't matter — your plan is on file and booking is a phone call or a few clicks. Cooling-off applies where guidelines require it, always.",
  },
  {
    title: "Your privacy",
    body: "Your consultation is confidential, and your record is a medical record — held to the same standard as any other in the building. The practice sits inside an established medical centre, so arriving here is no different from arriving for any other appointment. Nothing about your care is shared, published or discussed beyond the people providing it.",
  },
];

const FirstVisit = () => (
  <>
    <SEO title="Your First Visit | Lumière Aesthetics, Cottesloe" description="What to expect at your first visit: a 45-minute consultation with a doctor, a written plan, and no treatment pressure. Cottesloe, WA." />
    <PageBand
      eyebrow="First Visit"
      title="What to expect at your first visit"
      standfirst="A conversation with a doctor, a written plan, and no pressure — that's the whole visit."
      image={PAGE_HEROES.firstVisit}
    />

    <section className="bg-warm-white" data-testid="first-visit-sections">
      <div className="max-w-[68ch] mx-auto px-8 py-24 space-y-14">
        {SECTIONS.map((s, i) => (
          <Reveal key={s.title}>
            <div data-testid={`first-visit-section-${i}`}>
              <p className="eyebrow mb-3">{String(i + 1).padStart(2, "0")}</p>
              <h2 className="text-deep-sea">{s.title}</h2>
              <p className="text-ink/85 leading-[1.8] text-[17px] mt-4">{s.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="bg-cream" data-testid="first-visit-faq">
      <div className="max-w-3xl mx-auto px-8 py-24">
        <Reveal>
          <div className="text-center mb-10">
            <h2 className="text-deep-sea">Asked before nearly every first visit</h2>
            <GoldRule className="mx-auto mt-6" />
          </div>
          <Accordion type="single" collapsible className="w-full">
            {FIRST_VISIT_FAQS.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-hairline/40" data-testid={`fv-faq-${i}`}>
                <AccordionTrigger className="faq-q font-normal text-[20px] md:text-[22px] text-left hover:text-gold transition-colors duration-300 py-5">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-ink/75 text-sm leading-[1.8] pb-5">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>

    <CTABand />
  </>
);

export default FirstVisit;
